import { offsetDate, normalizeOffset } from '../../../utils/timezone';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiClock, FiGift, FiShield, FiTrendingUp, FiCreditCard } from 'react-icons/fi';
import { useMetricsContext } from '../../../contexts/Metrics-Context';
import { useSymbolContext } from '../../../contexts/Symbol-Context';
import { API_ENDPOINT_USER_DETAILS } from '../../../data/Endpoints-API';
import APIMiddleware from '../../../data/api/Api-Middleware';
import { webSocketManagerGlobal } from '../../../data/websocket/Websocket-Manager';
import './AccountStatsBar.scss';
import en from '../../../locales/en/translation.json';

const metricTips = {
  balance: 'tipBalance', equity: 'tipEquity', margin: 'tipMargin',
  freeMargin: 'tipFreeMargin', bonus: 'tipBonus', marginLevel: 'tipMarginLevel',
  floatingPnl: 'tipUnRNetPnl', realizedPnl: 'tipRealizedPnl', stopOut: 'tipStopOut',
};

export function formatStatsCurrency(value, symbol, rate, signed = false, locale = 'en-US') {
  if (!symbol || value == null || value === '' || !Number.isFinite(Number(value)) || !Number.isFinite(Number(rate)) || Number(rate) <= 0) return '—';
  const amount = Number(value) * Number(rate);
  const rounded = Math.round(Math.abs(amount) * 100) / 100;
  const sign = rounded > 0 && amount < 0 ? '−' : signed ? '+' : '';
  return `${sign}${symbol} ${rounded.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getConnectionState() {
  if (!navigator.onLine) return 'offline';
  const sockets = [...webSocketManagerGlobal.connections.values()];
  if (sockets.some(socket => socket.readyState === 1)) return 'connected';
  if (sockets.some(socket => socket.readyState === 0)) return 'connecting';
  return 'offline';
}

// Separate presentation keeps the reference layout testable without an account.
export function AccountStatsBarView({ metrics = {}, rate = 1, now, connection, utcOffset = '+00:00', showStopOut = false, stopOut = null, t = (_key, fallback) => fallback, locale = 'en-US' }) {
  const symbol = metrics.userCurrencySymbol;
  const cash = (value, signed = false) => formatStatsCurrency(value, symbol, rate, signed, locale);
  const pnlTone = value => Number(value) > 0 ? 'positive' : Number(value) < 0 ? 'negative' : '';
  const marginLevel = symbol && Number(metrics.totalMargin) > 0 && metrics.marginLevel != null && Number.isFinite(Number(metrics.marginLevel))
    ? `${Number(metrics.marginLevel).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : '—';
  const items = [
    ['balance', 'Balance', cash(metrics.balance), FiCreditCard, ''],
    ['equity', 'Equity', cash(metrics.equity), FiTrendingUp, 'equity'],
    ['margin', 'Margin', cash(metrics.totalMargin), FiShield, ''],
    ['freeMargin', 'Free margin', cash(metrics.freeMargin), null, ''],
    ['bonus', 'Bonus', cash(metrics.totalBonus), FiGift, ''],
    ['marginLevel', 'Margin level', marginLevel, FiActivity, ''],
    ['floatingPnl', 'Unrealized P/L', cash(metrics.totalUnrealizedPnL, true), null, pnlTone(metrics.totalUnrealizedPnL)],
    ['realizedPnl', 'Realised P/L', cash(metrics.totalRealizedPnL, true), null, pnlTone(metrics.totalRealizedPnL)],
  ];
  if (showStopOut) {
    const level = stopOut !== null && stopOut !== undefined && stopOut !== '' && Number.isFinite(Number(stopOut))
      ? `${Number(stopOut).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : '—';
    items.push(['stopOut', t('metrics.stopOut', 'Stop Out'), level, FiShield, '']);
  }
  const statusLabel = connection === 'connected'
    ? t('statsBar.live', 'Live')
    : connection === 'connecting' ? t('statsBar.reconnecting', 'Reconnecting') : t('statsBar.offline', 'Offline');
  return (
    <footer className="account-stats-bar" aria-label={t('statsBar.label', 'Account statistics')}>
      <div className="account-stats-bar__metrics">
        <dl>
          {items.map(([key, label, value, Icon, tone]) => (
            <div className="account-stats-bar__item" key={key} tabIndex={0}
              title={t(`metrics.${metricTips[key]}`, en.metrics[metricTips[key]])}>
              <dt>{Icon && <Icon aria-hidden="true" />}<span>{t(`statsBar.${key}`, label)}</span></dt>
              <dd className={tone} data-tooltip={value}>{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="account-stats-bar__status">
        <span className="account-stats-bar__clock"><FiClock aria-hidden="true" />{'UTC' + normalizeOffset(utcOffset)} <span aria-hidden="true">·</span> <time dateTime={now.toISOString()}>{offsetDate(now, utcOffset).toISOString().slice(11, 19)}</time></span>
        <span className={`account-stats-bar__connection ${connection}`} title={statusLabel} aria-label={statusLabel}>
          <i aria-hidden="true" /><span aria-hidden="true">{statusLabel}</span>
        </span>
      </div>
    </footer>
  );
}

export default function AccountStatsBar() {
  const { metrics, userCurrencyExchangeRate, utcOffset } = useMetricsContext();
  const { symbolInfo, fetchUserDetails2 } = useSymbolContext();
  const showStopOut = [true, 1, '1', 'true'].includes(symbolInfo?.show_stop_out_details);
  const [stopOut, setStopOut] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setStopOut(null);
    if (showStopOut) {
      APIMiddleware.post(API_ENDPOINT_USER_DETAILS())
        .then(response => { if (!cancelled) setStopOut(response?.data?.[0]?.stop_out ?? null); })
        .catch(() => { if (!cancelled) setStopOut(null); });
    }
    return () => { cancelled = true; };
  }, [showStopOut, fetchUserDetails2]);
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [connection, setConnection] = useState(getConnectionState);
  useEffect(() => {
    const refresh = () => { setNow(new Date()); setConnection(getConnectionState()); };
    const timer = window.setInterval(refresh, 1000);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    document.addEventListener('connectionOpened', refresh);
    document.addEventListener('connectionClosed', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
      document.removeEventListener('connectionOpened', refresh);
      document.removeEventListener('connectionClosed', refresh);
    };
  }, []);
  return createPortal(<AccountStatsBarView metrics={metrics} rate={userCurrencyExchangeRate} now={now} utcOffset={utcOffset} connection={connection} showStopOut={showStopOut} stopOut={stopOut} t={t} locale={i18n.language === 'it' ? 'it-IT' : 'en-US'} />, document.body);
}
